import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import { Field, Spinner } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";

/* =========================================================================
   INGRESO
   Dos columnas: a la izquierda la identidad del producto, a la derecha el
   formulario. Todo el texto va en español — la app móvil todavía arrastra
   "Log In", "LOG IN" y "Remember me" en la pantalla equivalente.
   ======================================================================== */

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verPass, setVerPass] = useState(false);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  /*
   * El código del segundo factor NO se pide de entrada.
   *
   * La mayoría de las cuentas no lo tiene activo, y un campo más en el
   * formulario de todos para la minoría que lo usa es ruido. El servidor
   * responde `two_factor_required` cuando hace falta y entonces aparece —con la
   * contraseña ya validada, así que no hay nada que repetir.
   */
  const [pideCodigo, setPideCodigo] = useState(false);
  const [codigo, setCodigo] = useState("");

  if (isAuthenticated) {
    return <Navigate to={location.state?.from ?? "/"} replace />;
  }

  const enviar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    setEnviando(true);
    const res = await login(email.trim(), password, codigo.trim() || undefined);
    setEnviando(false);

    if (res.ok) {
      navigate(location.state?.from ?? "/", { replace: true });
      return;
    }

    // `two_factor_required` no es un error de credenciales: es el siguiente
    // paso. Enseñarlo como fallo haría que la persona dudara de su contraseña.
    if (res.motivo === "two_factor_required") {
      setPideCodigo(true);
      setError(null);
      return;
    }

    if (res.motivo === "two_factor_invalid") {
      setPideCodigo(true);
    }

    setError(res.message);
  };

  return (
    <div className="auth-screen">
      <aside className="auth-aside">
        {/* Sobre el fondo de marca va la versión blanca del logotipo: la
            máscara del panel usa el color del tema, que aquí no aplica. */}
        {/* `alignSelf` es imprescindible: el aside es una columna flex y sin
            eso la imagen se estira a todo el ancho y sale deformada. */}
        <img
          src="/logotipo-blanco.png"
          alt="VeciPa'Ya"
          style={{
            height: 54,
            width: 86,
            alignSelf: "flex-start",
            objectFit: "contain",
          }}
        />

        <div>
          <h1 className="auth-claim">El barrio entero, en un solo tablero.</h1>
          <p className="auth-sub">
            Negocios, domiciliarios, pedidos y pagos de la operación completa,
            administrados desde un único lugar.
          </p>
        </div>

        <div className="auth-stats">
          <div>
            <div className="auth-stat-v">14</div>
            <div className="auth-stat-l">Módulos</div>
          </div>
          <div>
            <div className="auth-stat-v">4</div>
            <div className="auth-stat-l">Roles</div>
          </div>
          <div>
            <div className="auth-stat-v">100%</div>
            <div className="auth-stat-l">Trazado</div>
          </div>
        </div>
      </aside>

      <div className="auth-panel">
        <form className="auth-form" onSubmit={enviar}>
          <div>
            <h2 style={{ fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              Iniciar sesión
            </h2>
            <p className="muted" style={{ marginTop: 4 }}>
              Acceso exclusivo para cuentas de administración.
            </p>
          </div>

          {error && (
            <div className="auth-alert" role="alert">
              <Icon
                name="alert"
                size={17}
                style={{ flex: "none", marginTop: 1 }}
              />
              <span>{error}</span>
            </div>
          )}

          <Field label="Correo electrónico">
            <input
              className="input"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@vecipaya.com"
            />
          </Field>

          <Field label="Contraseña">
            <div style={{ position: "relative" }}>
              <input
                className="input"
                type={verPass ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{ paddingRight: 42 }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                onClick={() => setVerPass((v) => !v)}
                aria-label={
                  verPass ? "Ocultar contraseña" : "Mostrar contraseña"
                }
                style={{
                  position: "absolute",
                  right: 4,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Icon name="eye" size={16} />
              </button>
            </div>
          </Field>

          {pideCodigo && (
            <Field
              label="Código de verificación"
              hint="Los seis dígitos de tu aplicación de autenticación. Si perdiste el teléfono, escribe uno de tus códigos de recuperación."
            >
              <input
                className="input mono"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="000000"
                autoComplete="one-time-code"
                inputMode="text"
                autoFocus
              />
            </Field>
          )}

          <button
            className="btn btn-primary"
            type="submit"
            disabled={enviando}
            style={{ padding: 12 }}
          >
            {enviando ? (
              <Spinner size={15} />
            ) : (
              <Icon name="logout" size={16} />
            )}
            {enviando ? "Verificando…" : pideCodigo ? "Confirmar" : "Entrar"}
          </button>

          <p
            className="faint"
            style={{ fontSize: "var(--fs-xs)", textAlign: "center" }}
          >
            ¿Olvidaste tu contraseña? Solicita el restablecimiento al equipo
            técnico.
          </p>
        </form>
      </div>
    </div>
  );
}
